import React from 'react'
import { Trans, useTranslation } from '@mc/i18n'
import { PlansTier, TableFeature, useTiersInfo } from '@mc/plans-ui'
import { PLAN_Z_BOGO_I18N_NAMESPACE } from '../../../i18n'

interface PricingCellProps {
  tier: PlansTier
  /** Premium-only: show the "50% Off + Gift Plan" green label instead of the standard discount. */
  giftPlan?: boolean
}

const PricingCell = ({
  tier: { displayMonthlyPrice, displayMonthlyPriceWithDiscount, product },
  giftPlan = false,
}: PricingCellProps) => {
  const { t } = useTranslation(PLAN_Z_BOGO_I18N_NAMESPACE)
  const hasDiscountPrice = !!displayMonthlyPriceWithDiscount
  const discount = Math.round(product?.coupon?.percentage || 0)

  if (!hasDiscountPrice) {
    return (
      <span
        aria-label={t('matrix.features.pricing.cell.price', {
          price: displayMonthlyPrice,
        })}
      >
        {displayMonthlyPrice}
      </span>
    )
  }

  return (
    <div className='d-flex flex-column'>
      <span className='mc-text--success mc-text--bold mc-text-x-small mc-mb-1'>
        <Trans
          i18nKey='matrix.features.pricing.cell.discountShow'
          ns={PLAN_Z_BOGO_I18N_NAMESPACE}
          values={{ discount }}
        >
          <br />
        </Trans>
        {giftPlan ? t('matrix.features.pricing.cell.giftPlan') : null}
      </span>
      <span className='d-flex flex-wrap justify-content-center'>
        <span
          aria-label={t('matrix.features.pricing.cell.previous', {
            previousPrice: displayMonthlyPrice,
          })}
          className='mc-mx-1 mc-text-color--gray-500 mc-text--strike'
        >
          {displayMonthlyPrice}
        </span>
        <span
          aria-label={t('matrix.features.pricing.cell.current', {
            price: displayMonthlyPriceWithDiscount,
          })}
          className='mc-text--bold'
        >
          {displayMonthlyPriceWithDiscount}
        </span>
      </span>
    </div>
  )
}

export const PlanZPricingFeature = () => {
  const { t } = useTranslation(PLAN_Z_BOGO_I18N_NAMESPACE)
  const { individual, duo, family } = useTiersInfo()

  return (
    <TableFeature.Feature className='no-border mc-mb-0'>
      <TableFeature.Label title={t('matrix.features.pricing.title')} />
      <TableFeature.Individual>
        <PricingCell tier={individual} />
      </TableFeature.Individual>
      <TableFeature.Duo>
        <PricingCell tier={duo} />
      </TableFeature.Duo>
      <TableFeature.Family>
        <PricingCell tier={family} giftPlan />
      </TableFeature.Family>
    </TableFeature.Feature>
  )
}
